/* ============================================================
   Sophia HC — Hero Drawing
   1. draw every .hd-line stroke on, in DOM order, using stroke-dash
   2. crossfade to the .hd-finish layer (traced pencil)
   3. wag the .hd-tail group
   ============================================================ */
(() => {
  const NS = 'http://www.w3.org/2000/svg';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();

  class HeroDrawing {
    constructor(section) {
      this.section = section;
      this.svg = section.querySelector('svg.hd-svg');
      this.lines = this.svg ? Array.from(this.svg.querySelectorAll('.hd-line')) : [];
      this.duration = parseInt(section.dataset.drawDuration, 10) || 2500;
      this.finishDelay = parseInt(section.dataset.finishDelay, 10) || 0;
      this.wag = section.dataset.tailWag === 'true';
      this.showPen = section.dataset.showPen === 'true';
      this.strokeWidth = parseFloat(getComputedStyle(section).getPropertyValue('--hd-stroke-width')) || 2.5;
      this.raf = 0;
      this.timers = [];
      this.pen = null;
      this.penR = 3;
      this.onResize = () => this.syncStrokeWidth();
      window.addEventListener('resize', this.onResize);
    }

    /* Convert the CSS-pixel stroke width into viewBox units so lines look
       the same weight at every viewport size (dash animation needs real units). */
    /* The theme only sets --header-height for sticky headers, so measure it ourselves. */
    syncHeaderHeight() {
      const header = document.querySelector('.shopify-section-group-header-group') || document.querySelector('.section-header');
      const h = header ? header.offsetHeight : 0;
      this.section.style.setProperty('--hd-header-height', `${h}px`);
    }

    syncStrokeWidth() {
      if (!this.svg) return;
      this.syncHeaderHeight();
      const vb = this.svg.viewBox.baseVal;
      const rect = this.svg.getBoundingClientRect();
      if (!vb || !vb.width || !rect.width || !rect.height) return;
      const k = Math.max(vb.width / rect.width, vb.height / rect.height);
      this.svg.style.setProperty('--hd-sw-units', (this.strokeWidth * k).toFixed(3));
      this.penR = 3.2 * k;
      if (this.pen) this.pen.setAttribute('r', this.penR.toFixed(2));
    }

    start() {
      const s = this.section;
      s.classList.remove('is-finished', 'is-wagging', 'is-drawing');
      this.syncStrokeWidth();

      if (reduced.matches || !this.lines.length) {
        this.finish(true);
        return;
      }

      const items = this.lines.map((p) => {
        const L = p.getTotalLength();
        p.style.strokeDasharray = `${L} ${L}`;
        p.style.strokeDashoffset = L;
        return { p, L, done: false };
      });

      const total = items.reduce((a, i) => a + i.L, 0) || 1;
      const lift = 6;   // ms "pen lift" between strokes
      const minDur = 14; // ms floor so tiny strokes still register
      let raw = items.map((it) => Math.max(minDur, (this.duration * it.L) / total));
      const rawSum = raw.reduce((a, b) => a + b, 0) + lift * items.length;
      const fit = this.duration / rawSum; // squeeze so the whole thing takes ~duration
      let t = 0;
      items.forEach((it, i) => {
        it.start = t;
        it.dur = raw[i] * fit;
        t += it.dur + lift * fit;
      });
      this.items = items;
      this.end = t;

      if (this.showPen) {
        const c = document.createElementNS(NS, 'circle');
        c.setAttribute('class', 'hd-pen');
        c.setAttribute('r', this.penR.toFixed(2));
        c.style.opacity = 0;
        this.svg.appendChild(c);
        this.pen = c;
      }

      s.classList.add('is-drawing');
      this.t0 = performance.now();
      this.boundFrame = (now) => this.frame(now);
      this.raf = requestAnimationFrame(this.boundFrame);
    }

    frame(now) {
      const e = now - this.t0;
      let active = null;
      let k = 0;

      for (const it of this.items) {
        if (it.done) continue;
        if (e >= it.start + it.dur) {
          it.p.style.strokeDashoffset = 0;
          it.done = true;
          continue;
        }
        if (e >= it.start) {
          k = (e - it.start) / it.dur;
          k = 1 - Math.pow(1 - k, 1.35); // slight ease-out per stroke
          it.p.style.strokeDashoffset = it.L * (1 - k);
          active = it;
        }
        break;
      }

      if (this.pen) {
        if (active) {
          const pt = active.p.getPointAtLength(active.L * k);
          this.pen.setAttribute('cx', pt.x.toFixed(2));
          this.pen.setAttribute('cy', pt.y.toFixed(2));
          this.pen.style.opacity = 1;
        } else {
          this.pen.style.opacity = 0.35;
        }
      }

      if (e < this.end) {
        this.raf = requestAnimationFrame(this.boundFrame);
      } else {
        this.items.forEach((it) => { it.p.style.strokeDashoffset = 0; });
        this.timers.push(setTimeout(() => this.finish(false), this.finishDelay));
      }
    }

    finish(instant) {
      const s = this.section;
      if (this.pen) { this.pen.remove(); this.pen = null; }
      this.lines.forEach((p) => {
        p.style.strokeDasharray = '';
        p.style.strokeDashoffset = '';
      });
      s.classList.remove('is-drawing');
      if (instant) s.classList.add('no-transition');
      s.classList.add('is-finished');
      if (instant) requestAnimationFrame(() => s.classList.remove('no-transition'));
      this.syncStrokeWidth();
      if (this.wag && !reduced.matches) {
        this.timers.push(setTimeout(() => s.classList.add('is-wagging'), instant ? 0 : 750));
      }
    }

    destroy() {
      cancelAnimationFrame(this.raf);
      this.timers.forEach(clearTimeout);
      this.timers = [];
      window.removeEventListener('resize', this.onResize);
      if (this.pen) { this.pen.remove(); this.pen = null; }
    }
  }

  function mount(section) {
    const old = instances.get(section);
    if (old) old.destroy();
    const hd = new HeroDrawing(section);
    instances.set(section, hd);
    hd.start();
  }

  function boot() {
    document.querySelectorAll('[data-hero-drawing]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-hero-drawing]').forEach(mount);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-hero-drawing]').forEach((s) => {
      const i = instances.get(s);
      if (i) i.destroy();
    });
  });
})();
